// @flow
import { getPublicMessage } from 'api'
import {
  createAppointment,
  getAppointmentSettings,
  getSchedulerData,
  type SchedulerData,
} from 'api/scheduler'
import FormClientFooter from 'components/FormCustomer/FormClientFooter'
import Loading from 'components/Loading'
import moment from 'moment-timezone'
import * as React from 'react'
// $FlowFixMe - Flow doesn't know about this package yet
import { ScheduleMeeting } from 'react-schedule-meeting'
import { toast } from 'react-toastify'
import type { AppointmentSettings } from 'types/appointment-settings'
import type { Message } from 'types/message'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import Page from 'ui/organisms/Page'
import { ZentakeContext } from 'utils/ZentakeContext'

import TimeSlotDescription from './TimeSlotDescription'

type Props = {
  history: {
    location: {
      pathname: string,
    },
    push: (path: string, state?: any) => void,
  },
  match: {
    params: {
      messageCode: string,
    },
  },
}

const userTz = moment.tz.guess()

function convertToLocalDateTime(
  utcDateString: string,
  utcTimeString: string,
  fromTz: string = 'UTC',
  toTz: string = 'UTC'
) {
  const dateTimeString = `${utcDateString}T${utcTimeString}`
  let dateTime

  if (fromTz === 'UTC') {
    dateTime = moment.utc(dateTimeString, 'YYYY-MM-DDTHH:mm:ss')
  } else {
    dateTime = moment.tz(dateTimeString, fromTz)
  }

  if (toTz === 'UTC') {
    return dateTime.utc()
  }

  return dateTime.tz(toTz)
}

function Scheduler({ history, match }: Props): React.Node {
  const { setAlertConfig } = React.useContext(ZentakeContext)

  const [appointmentSettings, setAppointmentSettings] =
    React.useState<?AppointmentSettings>(null)
  const [availableTimeslots, setAvailableTimeslots] = React.useState<
    Array<{ endTime: Date, id: number, startTime: Date }>
  >([])
  const [error, setError] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [message, setMessage] = React.useState<?Message>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = React.useState<?Date>(null)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    function buildTimeSlots(schedulerData: SchedulerData) {
      const _availableTimeSlots = schedulerData.available_time_slots.map(
        (timeSlot, index) => ({
          id: index + 1,
          startTime: convertToLocalDateTime(
            timeSlot.date,
            timeSlot.start_time,
            'UTC',
            userTz
          ),
          endTime: convertToLocalDateTime(
            timeSlot.end_date,
            timeSlot.end_time,
            'UTC',
            userTz
          ),
        })
      )

      setAvailableTimeslots(_availableTimeSlots)
    }

    async function fetchPublicMessage() {
      const { messageCode } = match.params

      try {
        const data = await getPublicMessage(messageCode)
        setMessage(data)
      } catch (e) {
        setError(true)
      }
    }

    async function fetchSchedulerData(
      appointmentSettings: ?AppointmentSettings
    ) {
      if (!appointmentSettings?.appointment_type.id) return

      const { messageCode } = match.params

      try {
        const data = await getSchedulerData({
          appointment_type_id: appointmentSettings?.appointment_type.id,
          start_date: moment().format('YYYY-MM-DD'),
          message_code: messageCode,
        })
        buildTimeSlots(data)
      } catch (e) {
        setError(true)
      }
    }

    async function fetchAppointmentSettings() {
      const { messageCode } = match.params

      try {
        const data = await getAppointmentSettings(messageCode)
        setAppointmentSettings(data)
        return data
      } catch (e) {
        setError(true)
      }
    }

    async function fetchAllData() {
      await fetchPublicMessage()
      const appointmentSettings = await fetchAppointmentSettings()
      await fetchSchedulerData(appointmentSettings)

      setLoading(false)
    }

    fetchAllData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isPacket = Boolean(message?.packet_response)
  const { messageCode } = match.params

  const redirectToResource = React.useCallback(() => {
    history.push(
      `/r/${messageCode}?response_type=${isPacket ? 'packet' : 'form'}`
    )
  }, [history, isPacket, messageCode])

  const doRedirect = React.useCallback(
    (justScheduled = false) => {
      const resource = isPacket
        ? message?.packet_response?.packet
        : message?.response?.form
      if (!resource) return
      const response = isPacket ? message?.packet_response : message?.response
      const responseSubmitted = response?.is_submitted
      const responseScheduled = justScheduled || response?.is_scheduled
      const scheduleBeforeSubmit = resource.settings?.schedule_before_submit
      const requireAppointment = resource.settings?.require_appointment

      if (!requireAppointment) {
        redirectToResource()
        return
      }

      if (scheduleBeforeSubmit && responseScheduled) {
        redirectToResource()

        return
      }

      if (!scheduleBeforeSubmit && !responseSubmitted) {
        redirectToResource()
        return
      }

      if (responseSubmitted && responseScheduled) {
        history.push(`/s/${messageCode}/success`, {
          timeslot: selectedTimeSlot,
          duration: appointmentSettings?.appointment_type.default_duration,
        })
      }
    },
    [
      appointmentSettings?.appointment_type.default_duration,
      history,
      isPacket,
      message?.packet_response,
      message?.response,
      messageCode,
      redirectToResource,
      selectedTimeSlot,
    ]
  )

  React.useEffect(() => {
    doRedirect()
  }, [doRedirect])

  const scheduleAppointment = React.useCallback(async () => {
    if (!selectedTimeSlot) return
    if (!appointmentSettings?.appointment_type.id) return

    const { messageCode } = match.params

    const serverTimeSlot = convertToLocalDateTime(
      selectedTimeSlot.toISOString().split('T')[0],
      selectedTimeSlot.toISOString().split('T')[1],
      userTz,
      'UTC'
    )

    try {
      setSubmitting(true)

      await createAppointment({
        appointment_type: appointmentSettings?.appointment_type.id,
        message_code: messageCode,
        scheduled_date: serverTimeSlot.format(),
      })

      doRedirect(true)
    } catch (e) {
      toast.error('An error has occurred')
    }
    setSubmitting(false)
  }, [
    appointmentSettings?.appointment_type.id,
    doRedirect,
    match.params,
    selectedTimeSlot,
  ])

  const confirmScheduleAppointment = React.useCallback(async () => {
    if (!appointmentSettings) return

    await setAlertConfig({
      title: appointmentSettings.appointment_type.name,
      description: (
        <TimeSlotDescription
          duration={appointmentSettings.appointment_type.default_duration}
          timeslot={selectedTimeSlot}
        />
      ),
      buttons: [
        {
          title: 'Back',
          style: 'secondary',
        },
        {
          title: 'Confirm',
          style: 'primary',
          onPress: scheduleAppointment,
        },
      ],
    })
  }, [
    appointmentSettings,
    scheduleAppointment,
    selectedTimeSlot,
    setAlertConfig,
  ])

  if (error) return 'An error has occurred'

  if (loading) return <Loading skipLogoutMessage />

  return (
    <Page title="Select a Date & Time for your First Visit">
      <Space size="large">
        <div>
          <Text>
            Find an appointment time below that works for you. Your first visit
            will be a 1.5 hour appointment with a Willow Health therapist and
            psychiatric clinician.
          </Text>
        </div>
        <div>
          <Text>
            If you do not see any appointments available, please call our Front
            Desk at 646-814-1530 between the hours of 11am and 7pm. You can now
            close this window. The patient information has been submitted.
          </Text>
        </div>
        <div>
          <Text>
            If this is a life-threatening emergency, please call 911 or the
            Suicide and Crisis Lifeline at 988.
          </Text>
        </div>
      </Space>
      <ScheduleMeeting
        availableTimeslots={availableTimeslots}
        borderRadius={10}
        eventDurationInMinutes={
          appointmentSettings?.appointment_type.default_duration
        }
        onStartTimeSelect={({ startTime }) => {
          setSelectedTimeSlot(startTime)
        }}
        primaryColor="#1d8cf8"
      />
      <FormClientFooter
        currentStep={0}
        loading={submitting}
        onSubmit={confirmScheduleAppointment}
        submitDisabled={!selectedTimeSlot}
        submitLabel="Schedule Appointment"
        submittingSave={submitting}
        totalSteps={0}
      />
    </Page>
  )
}

export default Scheduler
